import { Link } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { Badge, Button, EmptyState, InfoBox, LoadingState } from '@biznomi/ui-react'
import { getLeaves, leaveTypeLabel, type LeaveRecord } from '@/lib/api/leave'
import { getMyMonthlyStats } from '@/lib/api/attendance'

/** 고른 날짜를 사람이 읽는 형태로. 옛 기록은 dates 가 없어 기간으로 보여준다 */
function daysText(r: LeaveRecord): string {
  if (r.dates?.length) {
    return r.dates.map((d) => d.slice(5, 10).replace('-', '/')).join(', ')
  }
  const s = r.startDate.slice(5, 10).replace('-', '/')
  const e = r.endDate.slice(5, 10).replace('-', '/')
  return s === e ? s : `${s} ~ ${e}`
}

function StatusBadge({ s }: { s: LeaveRecord['status'] }) {
  if (s === 'APPROVED') return <Badge variant="info">승인</Badge>
  if (s === 'REJECTED') return <Badge variant="destructive">반려</Badge>
  if (s === 'CANCELLED') return <Badge variant="outline">취소</Badge>
  return <Badge variant="warning">승인 대기</Badge>
}

export default function Leave() {
  const { data, isLoading } = useQuery({
    queryKey: ['leave', 'mine'],
    queryFn: () => getLeaves(),
  })
  const { data: summary } = useQuery({
    queryKey: ['attendance', 'my-monthly-stats'],
    queryFn: getMyMonthlyStats,
  })

  const rows = data?.success ? data.data ?? [] : []
  const bal = summary?.success ? summary.data : undefined

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">휴가</h1>
        <Button asChild size="sm">
          <Link to="/leave/new">신청</Link>
        </Button>
      </div>

      {/* 잔여는 회사가 넣은 값에서 쓴 만큼 뺀 것이다. 발생 일수를 계산하지 않는다 */}
      <div className="grid grid-cols-3 gap-2 rounded-card bg-white p-4">
        {[
          ['부여', bal?.leaveTotalDays],
          ['사용', bal?.leaveUsedDays],
          ['남음', bal?.leaveRemainingDays],
        ].map(([label, v], i) => (
          <div key={label as string} className="text-center">
            <p className="text-xs text-slate-500">{label as string}</p>
            <p className={i === 2 ? 'text-xl font-bold text-primary' : 'text-xl font-bold'}>
              {v ?? 0}<span className="text-sm font-normal text-slate-400">일</span>
            </p>
          </div>
        ))}
      </div>

      {bal && bal.leaveTotalDays === 0 && (
        <InfoBox tone="info">
          아직 부여된 연차가 없습니다. 관리자가 입력하면 여기에 표시됩니다.
        </InfoBox>
      )}

      {isLoading ? (
        <LoadingState />
      ) : rows.length === 0 ? (
        <EmptyState title="신청한 휴가가 없습니다" hint="위 «신청» 을 눌러 시작하세요." />
      ) : (
        <ul className="divide-y divide-slate-100 rounded-card bg-white">
          {rows.map((r) => (
            <li key={r.id} className="flex items-start justify-between gap-3 px-4 py-3.5">
              <div className="min-w-0 space-y-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <Badge variant="purple">{leaveTypeLabel(r.type)}</Badge>
                  <StatusBadge s={r.status} />
                </div>
                <p className="text-sm text-slate-600">{daysText(r)}</p>
                {r.reason && <p className="text-xs text-slate-400">{r.reason}</p>}
                {r.rejectionReason && (
                  <p className="whitespace-pre-line text-xs text-[var(--color-danger)]">{r.rejectionReason}</p>
                )}
              </div>
              <span className="shrink-0 font-semibold tabular-nums">
                {r.days ?? (r.dates?.length || null) ?? '?'}일
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
